import { useMemo, useState } from 'react'
import type { AnswerMap, AnswerValue, WorkflowStage } from '../../types/workflow'

interface SmartMedicationPickerTemplateProps {
  stage: WorkflowStage
  answers: AnswerMap
  onFieldChange: (fieldId: string, value: AnswerValue) => void
}

interface MedicationItem {
  value: string
  label: string
  subtitle: string
}

interface MedicationCategory {
  id: string
  label: string
  items: MedicationItem[]
}

const MEDICATION_CATEGORIES: MedicationCategory[] = [
  {
    id: 'blood_pressure',
    label: 'Blood Pressure Medications',
    items: [
      { value: 'lisinopril', label: 'Lisinopril', subtitle: 'Prinivil, Zestril' },
      { value: 'amlodipine', label: 'Amlodipine', subtitle: 'Norvasc' },
      { value: 'losartan', label: 'Losartan', subtitle: 'Cozaar' },
      { value: 'metoprolol', label: 'Metoprolol', subtitle: 'Lopressor, Toprol-XL' },
      { value: 'hydrochlorothiazide', label: 'Hydrochlorothiazide', subtitle: 'HCTZ, Microzide' },
    ],
  },
  {
    id: 'thyroid',
    label: 'Thyroid Medications',
    items: [
      { value: 'levothyroxine', label: 'Levothyroxine', subtitle: 'Synthroid, Levoxyl' },
      { value: 'liothyronine', label: 'Liothyronine', subtitle: 'Cytomel' },
      { value: 'armour_thyroid', label: 'Armour Thyroid', subtitle: 'Natural desiccated thyroid' },
    ],
  },
  {
    id: 'diabetes',
    label: 'Diabetes Medications',
    items: [
      { value: 'metformin', label: 'Metformin', subtitle: 'Glucophage' },
      { value: 'insulin', label: 'Insulin', subtitle: 'Various' },
      { value: 'glipizide', label: 'Glipizide', subtitle: 'Glucotrol' },
      { value: 'empagliflozin', label: 'Empagliflozin', subtitle: 'Jardiance' },
    ],
  },
  {
    id: 'mental_health',
    label: 'Mental Health',
    items: [
      { value: 'sertraline', label: 'Sertraline', subtitle: 'Zoloft' },
      { value: 'fluoxetine', label: 'Fluoxetine', subtitle: 'Prozac' },
      { value: 'escitalopram', label: 'Escitalopram', subtitle: 'Lexapro' },
      { value: 'bupropion', label: 'Bupropion', subtitle: 'Wellbutrin' },
      { value: 'trazodone', label: 'Trazodone', subtitle: 'Desyrel' },
    ],
  },
  {
    id: 'cholesterol',
    label: 'Cholesterol Medications',
    items: [
      { value: 'atorvastatin', label: 'Atorvastatin', subtitle: 'Lipitor' },
      { value: 'rosuvastatin', label: 'Rosuvastatin', subtitle: 'Crestor' },
      { value: 'simvastatin', label: 'Simvastatin', subtitle: 'Zocor' },
    ],
  },
  {
    id: 'supplements',
    label: 'Vitamins & Supplements',
    items: [
      { value: 'vitamin_d', label: 'Vitamin D', subtitle: 'D3, Cholecalciferol' },
      { value: 'vitamin_b12', label: 'Vitamin B12', subtitle: 'Cyanocobalamin' },
      { value: 'iron', label: 'Iron supplement', subtitle: 'Ferrous sulfate' },
      { value: 'multivitamin', label: 'Multivitamin', subtitle: 'Various brands' },
    ],
  },
]

export default function SmartMedicationPickerTemplate({ answers, onFieldChange }: SmartMedicationPickerTemplateProps) {
  const initialSelection = useMemo(() => {
    const medicationData = answers?.medication_data
    if (!medicationData || typeof medicationData !== 'object' || Array.isArray(medicationData)) {
      return []
    }
    const meds = (medicationData as Record<string, unknown>).medications
    return Array.isArray(meds) ? meds.filter((med): med is string => typeof med === 'string') : []
  }, [answers])

  const [selected, setSelected] = useState<string[]>(initialSelection)
  const [search, setSearch] = useState('')
  const [expandedCat, setExpandedCat] = useState<string | null>(null)

  const toggle = (value: string) => {
    setSelected((prev) => (prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]))
  }

  const filteredCategories = useMemo(() => {
    if (!search.trim()) return MEDICATION_CATEGORIES
    const q = search.toLowerCase()
    return MEDICATION_CATEGORIES.map((category) => ({
      ...category,
      items: category.items.filter(
        (item) => item.label.toLowerCase().includes(q) || item.subtitle.toLowerCase().includes(q)
      ),
    })).filter((category) => category.items.length > 0)
  }, [search])

  const onContinue = () => {
    onFieldChange('medication_data', { medications: selected })
  }

  return (
    <div className="flex-1 flex flex-col gap-3 min-h-0">
      <input
        className="w-full py-3 px-4 text-[14px] text-[var(--ww-text)] bg-[var(--ww-surface)] border border-[var(--ww-glass-border)] rounded-xl outline-none transition-all duration-200 glass placeholder:text-[var(--ww-text-3)] focus:border-[var(--ww-accent)] focus:shadow-[0_0_0_3px_var(--ww-glow)] shrink-0"
        placeholder="Search medications..."
        aria-label="Search medications"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pb-1 shrink-0">
          {selected.map((medication) => (
            <span
              key={medication}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[12px] font-medium text-[var(--ww-accent)] bg-[var(--ww-accent-dim)] rounded-full border border-[rgba(74,138,154,0.15)] cursor-pointer hover:bg-[rgba(74,138,154,0.2)]"
              onClick={() => toggle(medication)}
            >
              {medication.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </span>
          ))}
        </div>
      )}

      <div className="flex-1 flex flex-col gap-1.5 overflow-y-auto scrollbar-hide min-h-0">
        {filteredCategories.map((category) => (
          <div key={category.id} className="bg-[var(--ww-surface)] border border-[var(--ww-glass-border)] rounded-xl overflow-hidden glass">
            <button
              className="w-full flex items-center justify-between py-3 px-4 text-left cursor-pointer bg-transparent border-none"
              onClick={() => setExpandedCat(expandedCat === category.id ? null : category.id)}
            >
              <span className="text-[14px] font-medium text-[var(--ww-text)]">{category.label}</span>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`text-[var(--ww-text-3)] transition-transform duration-200 ${expandedCat === category.id ? 'rotate-180' : ''}`}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {expandedCat === category.id && (
              <div className="border-t border-[var(--ww-border)]" style={{ maxHeight: '170px', overflow: 'auto' }}>
                {category.items.map((item) => (
                  <button
                    key={item.value}
                    className={`w-full flex items-center gap-3 py-2.5 px-4 text-left cursor-pointer bg-transparent border-none transition-colors hover:bg-[var(--ww-surface-2)] ${
                      selected.includes(item.value) ? 'bg-[var(--ww-accent-dim)]' : ''
                    }`}
                    onClick={() => toggle(item.value)}
                  >
                    <span
                      className={`flex items-center justify-center w-4 h-4 rounded border-[1.5px] shrink-0 transition-all duration-200 ${
                        selected.includes(item.value) ? 'bg-[var(--ww-accent)] border-[var(--ww-accent)]' : 'border-[var(--ww-text-3)]'
                      }`}
                    >
                      {selected.includes(item.value) && (
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </span>
                    <div>
                      <span className="text-[13px] font-medium text-[var(--ww-text)]">{item.label}</span>
                      {item.subtitle && <span className="ml-1.5 text-[11px] text-[var(--ww-text-3)]">{item.subtitle}</span>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="shrink-0 pt-4 pb-2 -ml-[42px] w-[calc(100%+42px)]">
        <button
          onClick={onContinue}
          className="w-full py-4 px-6 text-[15px] font-semibold text-[var(--ww-btn-text)] bg-[var(--ww-btn-bg)] border-none rounded-full cursor-pointer transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:bg-[var(--ww-btn-hover)] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.15)]"
        >
          {selected.length > 0 ? `Continue (${selected.length} selected)` : 'None \u2014 Continue'}
        </button>
      </div>
    </div>
  )
}
